import { Box, Button, Paper, Typography } from "@mui/material";
import React, { type MouseEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { IReportInfo } from "../../../api/types/reports/IReportInfo";
import { dateToComponents } from "../../../components/template/row/utils/DateToComponents";
import { truncateString } from "../../../components/template/row/utils/TruncateString";
import { Color } from "../../../constants/colors";
import { globalRadius } from "../../../constants/sizes";
import { PagePath } from "../../../navigation/routes";
import { useAppSelector } from "../../../store/hooks";
import {
  canManageTicketGrid,
  canViewTicketGridChildTemplates,
} from "../../../utils/userPermissions";
import { TicketGridRowControlButtons } from "../common/TicketGridRowControlButtons";

interface Params {
  info: IReportInfo;
}

interface FieldProps {
  label: string;
  children: ReactNode;
  span?: 1 | 2 | 3;
  inline?: boolean;
}

function Field({ label, children, span = 1, inline = false }: FieldProps) {
  if (inline) {
    return (
      <Typography
        variant="caption"
        component="div"
        lineHeight={1.3}
        sx={{ minWidth: 0, wordBreak: "break-word" }}
      >
        <Box component="span" color="text.secondary">
          {label}:{" "}
        </Box>
        {children}
      </Typography>
    );
  }

  return (
    <Box sx={{ minWidth: 0, gridColumn: span > 1 ? "1 / -1" : undefined }}>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        lineHeight={1.2}
        sx={{ mb: 0.25 }}
      >
        {label}
      </Typography>
      <Typography
        variant="caption"
        component="div"
        lineHeight={1.3}
        sx={{ wordBreak: "break-word" }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function stopPropagation(e: MouseEvent) {
  e.stopPropagation();
}

export function TicketGridTemplateCard({ info }: Params) {
  const navigate = useNavigate();
  const userType = useAppSelector((s) => s.user.info?.type);
  const canManageGrid = canManageTicketGrid(userType);
  const canOpenChildTemplates = canViewTicketGridChildTemplates(userType);

  const operators = useAppSelector((s) => {
    const ids = info.operators.split(",").map((id) => parseInt(id, 10));
    return (
      s.directory.operators
        ?.filter((o) => ids.includes(o.id))
        .map((o) => o.name) ?? []
    );
  });

  const visibleOperators = operators.slice(0, 3);
  const remainingOperatorsCount = operators.length - visibleOperators.length;
  const operatorsDisplay =
    remainingOperatorsCount > 0
      ? `${visibleOperators.join(", ")} +${remainingOperatorsCount}`
      : visibleOperators.join(", ");

  const disabled = useAppSelector((s) =>
    s.ticketGrids.loadingReportKeys.includes(`${info.id}`),
  );

  const openChildTemplates = () => {
    if (!canOpenChildTemplates) {
      return;
    }
    navigate(PagePath.ticketGrids.childTemplatesFor(info.id));
  };

  const openReport = (e: MouseEvent) => {
    stopPropagation(e);
    window.open(
      PagePath.ticketGrids.reportFor(info.id),
      "_blank",
      "noopener,noreferrer",
    );
  };

  const [lastUpdateDate, lastUpdateTime] = dateToComponents(info.last_update);

  const dateParts = info.dates.split(",").filter((d) => d.trim());
  const datesRange =
    dateParts.length > 0
      ? `${dateParts[0]}-${dateParts[dateParts.length - 1]}`
      : "";

  const fieldsGridSx = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 1,
    width: "100%",
  } as const;

  return (
    <Paper
      elevation={1}
      onClick={canOpenChildTemplates ? openChildTemplates : undefined}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        p: 1.5,
        cursor: canOpenChildTemplates ? "pointer" : "default",
        borderRadius: `${globalRadius}px`,
        transition: "box-shadow 0.2s ease-in-out",
        "&:hover": {
          boxShadow: 4,
        },
      }}
    >
      <Typography
        component="div"
        sx={{
          fontWeight: 700,
          fontSize: "0.85rem",
          textTransform: "uppercase",
          textAlign: "center",
          mb: 1,
          wordBreak: "break-word",
          lineHeight: 1.25,
        }}
      >
        {info.name} ({info.id})
      </Typography>

      <Box sx={{ ...fieldsGridSx, mb: 1 }}>
        <Field label="Город вылета">{info.city_from_string}</Field>
        <Field label="Страна">{info.country_string}</Field>
        <Field label="Операторы" span={3}>
          {operatorsDisplay}
        </Field>
      </Box>

      <Box sx={{ mb: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Field label="Ночей" inline>
          {truncateString(info.nights.split(",").join(", "), 50)}
        </Field>
        <Field label="Даты" inline>
          {datesRange}
        </Field>
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <Field label="Дата обновления" inline>
          {lastUpdateDate} {lastUpdateTime}
        </Field>
      </Box>

      <Box
        sx={{
          mt: "auto",
          display: "flex",
          alignItems: "center",
          gap: 1,
          width: "100%",
        }}
        onClick={stopPropagation}
      >
        <Button
          variant="contained"
          size="small"
          disabled={disabled}
          onClick={openReport}
          sx={{
            flex: 1,
            minWidth: 0,
            backgroundColor: Color.brand,
            borderRadius: `${globalRadius}px`,
            textTransform: "none",
            py: 0.5,
            "&:hover": {
              backgroundColor: Color.brand,
              filter: "brightness(0.9)",
            },
          }}
        >
          Открыть
        </Button>
        {canManageGrid ? (
          <TicketGridRowControlButtons disabled={disabled} info={info} />
        ) : null}
      </Box>
    </Paper>
  );
}
